import { yupResolver } from '@hookform/resolvers/yup';
import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import type { FileWithStatus } from '@ingenico/react-ui/file-handlerV2';
import type { UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import type { TFunction } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import { array, object, string } from 'yup';
import {
  RESOURCE_TEXT_FIELD_MAX_LENGTH,
  RESOURCE_TEXT_FIELD_MIN_LENGTH,
} from '../resources-list/ResourcesList.constants';

export interface CreateResourceFormValues {
  readonly name: string;
  readonly description: string;
  readonly files: FileWithStatus[];
}

const DEFAULT_VALUES: CreateResourceFormValues = {
  name: '',
  description: '',
  files: [],
};

function createSchema(t: TFunction) {
  return object({
    name: string()
      .trim()
      .required(t('error.required', { ns: ALL_LNGS_NAMESPACES.COMMON }))
      .min(
        RESOURCE_TEXT_FIELD_MIN_LENGTH,
        t('error.minLength', { ns: ALL_LNGS_NAMESPACES.COMMON, minLength: RESOURCE_TEXT_FIELD_MIN_LENGTH }),
      )
      .max(
        RESOURCE_TEXT_FIELD_MAX_LENGTH,
        t('error.maxLength', { ns: ALL_LNGS_NAMESPACES.COMMON, maxLength: RESOURCE_TEXT_FIELD_MAX_LENGTH }),
      ),
    description: string()
      .transform((value: unknown) => {
        if (typeof value === 'string') {
          const trimmed = value.trim();
          return trimmed === '' ? undefined : trimmed;
        }

        return value;
      })
      .optional()
      .min(
        RESOURCE_TEXT_FIELD_MIN_LENGTH,
        t('error.minLength', { ns: ALL_LNGS_NAMESPACES.COMMON, minLength: RESOURCE_TEXT_FIELD_MIN_LENGTH }),
      )
      .max(
        RESOURCE_TEXT_FIELD_MAX_LENGTH,
        t('error.maxLength', { ns: ALL_LNGS_NAMESPACES.COMMON, maxLength: RESOURCE_TEXT_FIELD_MAX_LENGTH }),
      ),
    files: array()
      .min(1, t('error.required', { ns: ALL_LNGS_NAMESPACES.COMMON }))
      .required(t('error.required', { ns: ALL_LNGS_NAMESPACES.COMMON })),
  }).required();
}

export function useCreateResourceModalForm(): UseFormReturn<CreateResourceFormValues> {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.COMMON, ALL_LNGS_NAMESPACES.RESOURCES]);

  return useForm<CreateResourceFormValues>({
    mode: 'onChange',
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(createSchema(t)),
  });
}
